import React, { useState, useRef, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/**
 * SearchBar Component
 * 
 * Search and replace functionality for transcript
 */
const SearchBar = ({
  onSearch,
  onReplace,
  onNext,
  onPrevious,
  results = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const searchInputRef = useRef(null);

  /**
   * Handle search
   */
  const handleSearch = (query) => {
    setSearchQuery(query);
    onSearch(query, caseSensitive);
  };

  /**
   * Handle replace
   */
  const handleReplace = () => {
    onReplace(searchQuery, replaceQuery, false, caseSensitive);
  };

  /**
   * Handle replace all
   */
  const handleReplaceAll = () => {
    onReplace(searchQuery, replaceQuery, true, caseSensitive);
  };

  /**
   * Focus input when opened
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Update search when case sensitivity changes
   */
  useEffect(() => {
    if (searchQuery) {
      onSearch(searchQuery, caseSensitive);
    }
  }, [caseSensitive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
      <div className="space-y-3">
        {/* Search Input */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search in transcript..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (e.shiftKey) {
                    onPrevious();
                  } else {
                    onNext();
                  }
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600 px-2">
              {results.length > 0 ? `${currentIndex + 1} / ${results.length}` : '0 / 0'}
            </span>

            <button
              onClick={onPrevious}
              disabled={results.length === 0}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous (Shift+Enter)"
            >
              <ChevronUpIcon className="h-5 w-5 text-gray-600" />
            </button>

            <button
              onClick={onNext}
              disabled={results.length === 0}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next (Enter)"
            >
              <ChevronDownIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Toggle Replace */}
          <button
            onClick={() => setShowReplace(!showReplace)}
            className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {showReplace ? 'Hide Replace' : 'Replace'}
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <XMarkIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Replace Input (conditional) */}
        {showReplace && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Replace with..."
              value={replaceQuery}
              onChange={(e) => setReplaceQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleReplace();
                }
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            <button
              onClick={handleReplace}
              disabled={!searchQuery || results.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Replace
            </button>

            <button
              onClick={handleReplaceAll}
              disabled={!searchQuery || results.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Replace All
            </button>
          </div>
        )}

        {/* Options */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Case sensitive
          </label>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;


